<script setup lang="ts">
import { computed } from "vue";
import type { PropType } from "vue";
import { ElementUiTableSortChangeFunction } from "~/types/element-ui/ElTableTypes";

const props = defineProps({
  // Accept an array or a Laravel paginator object ({ data: [...] }). el-table
  // requires an array, so we normalise below to avoid "data.includes is not a
  // function" crashes when a store passes the raw paginated response.
  tableData: {
    required: true,
    type: [Array, Object] as PropType<any>,
  },
  sortFn: {
    type: Function as PropType<ElementUiTableSortChangeFunction>,
    required: false,
    default: undefined,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  rowClassName: {
    type: Function,
    required: false,
    default: undefined,
  },
  maxHeight: {
    type: [Number, String] as PropType<number | string>,
    required: false,
    default: undefined,
  },
  showSummary: {
    type: Boolean,
    default: false,
  },
  summaryMethod: {
    type: Function as PropType<
      (param: { columns: any[]; data: any[] }) => string[]
    >,
    required: false,
    default: undefined,
  },
  spanMethod: {
    type: Function as PropType<
      (param: {
        row: any;
        column: any;
        rowIndex: number;
        columnIndex: number;
      }) => { rowspan: number; colspan: number } | undefined
    >,
    required: false,
    default: undefined,
  },
  // For a table whose show-overflow-tooltip cells hold something long enough
  // that the default one-line popper runs off the screen.
  tooltipOptions: {
    type: Object as PropType<Record<string, unknown>>,
    required: false,
    default: undefined,
  },
});

const maxHeight = props.maxHeight || "calc(100vh - 80px)";

// Always hand el-table a plain array, unwrapping a paginator object if needed.
const rows = computed(() =>
  Array.isArray(props.tableData)
    ? props.tableData
    : props.tableData?.data ?? [],
);

defineEmits(["selection-change"]);

// So a page that ticked rows can untick them again from the outside.
const multipleTable = ref();

defineExpose({
  clearSelection: () => multipleTable.value?.clearSelection(),
});
</script>
<template>
  <el-table
    ref="multipleTable"
    v-loading="props.loading"
    class="modern-el-table"
    :data="rows"
    :flexible="true"
    empty-text="No data"
    header-cell-class-name="table-header"
    :row-class-name="props.rowClassName"
    style="width: 100%"
    :max-height="maxHeight"
    :show-summary="props.showSummary"
    :summary-method="props.summaryMethod"
    :span-method="props.spanMethod"
    tooltip
    tooltip-effect="dark"
    :tooltip-options="props.tooltipOptions"
    @sort-change="props.sortFn"
    @selection-change="$emit('selection-change', $event)"
  >
    <slot></slot>
    <!-- Forwarded only when a caller actually supplies it. el-table's own
         `empty` fallback renders `empty-text` ("No data"), and it reaches that
         fallback via ensureValidVNode — which an unconditional forward of an
         unfilled slot does still satisfy, but only because a comment-only
         fragment counts as empty. Guarding on $slots keeps every other table in
         the app off that technicality. -->
    <template v-if="$slots.empty" #empty><slot name="empty" /></template>
  </el-table>
</template>
<style scoped>
.modern-el-table {
  border: 1px solid rgba(59, 42, 130, 0.12);
  border-radius: 14px;
  overflow: hidden;
}

/* Header: the brand purple, with the lettering doing the modernising rather
   than the ground. Uppercase 11px with real tracking reads as a column label;
   the 12px sentence case it replaced read as a title bar. The gradient is a
   single flat step now — two stops across a 40px header is detail nobody can
   resolve, and it banded against the pinned columns' own opaque fill.

   Note what is deliberately NOT changed below: the two hover rules keep their
   exact selector shape. `.due-today-row` and `.overdue-row` live in main.css and
   carry the full `.modern-el-table .el-table__body tr…` chain specifically to
   outrank this file's hover at (0,5,2) — with a `.dark` prefix they reach
   (0,6,2). Alter the weight here and both row highlights silently stop. */
.modern-el-table :deep(.el-table__header-wrapper th.el-table__cell) {
  background: var(--light-sec-purple) !important;
  color: #ffffff;
  border-bottom: 0;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.dark .modern-el-table :deep(.el-table__header-wrapper th.el-table__cell) {
  background: var(--light-purple-bar) !important;
}

.modern-el-table :deep(.el-table__header-wrapper th.el-table__cell .cell) {
  font-size: 11px;
}

/* Sort carets and the header text share one colour, so a sortable column does
   not read as a different kind of header from a plain one. */
.modern-el-table :deep(.el-table__header-wrapper th.el-table__cell .caret-wrapper) {
  color: rgb(255 255 255 / 0.75);
}

/* No frame around the table in dark mode — the purple-tinted hairline above is
   tuned for the light canvas and just reads as a stray box on #090d16.
   `border-color: transparent` rather than `border: none` so the box keeps its
   1px metrics and nothing shifts a pixel when the theme is toggled.

   The `dark` class sits on <body>, an ancestor of this component, and a plain
   descendant selector is exactly right for that: Vue attaches the scope
   attribute to the LAST selector, so this compiles to
   `.dark .modern-el-table[data-v-…]`. Do NOT reach for `:global(.dark)` here —
   Vue's scoped plugin replaces the whole selector with the contents of the
   parens, so `:global(.dark) .modern-el-table` silently compiles to a bare
   `.dark` and would strip the border off the entire page. */
.dark .modern-el-table {
  border-color: transparent;
}

.modern-el-table :deep(.el-table__inner-wrapper::before) {
  display: none;
}


.modern-el-table :deep(.el-table__body td.el-table__cell) {
  border-bottom: 1px solid var(--home-hairline);
}

/* Only the outer frame goes away in dark mode; rows still need separating, but
   the light theme's translucent purple over #191d23 reads as grime rather than
   a rule, so switch to the palette's own divider colour. */
.dark .modern-el-table :deep(.el-table__body td.el-table__cell) {
  border-bottom-color: var(--dark-primary-border);
}

.modern-el-table :deep(.el-table__body tr:hover > td.el-table__cell) {
  background-color: rgba(90, 85, 206, 0.08) !important;
}

/* Same hover treatment, lifted towards --light-purple-border and a little
   stronger: 8% of #5a55ce over a dark row is invisible. Has to repeat
   !important, because an important declaration outranks a more specific plain
   one. */
.dark .modern-el-table :deep(.el-table__body tr:hover > td.el-table__cell) {
  background-color: rgba(141, 135, 255, 0.12) !important;
}

/* Pinned cells are the exception, and they cannot take a translucent fill.
   A fixed column floats over the cells scrolling beneath it, so 12% alpha with
   no opaque base lets that content read straight through the pinned column.
   Same visible tint, painted as a gradient layer on top of an opaque base —
   the pattern the report tables already use for exactly this reason. */
.dark
  .modern-el-table
  :deep(.el-table__body tr:hover > td.el-table-fixed-column--left),
.dark
  .modern-el-table
  :deep(.el-table__body tr:hover > td.el-table-fixed-column--right) {
  background-color: var(--dark-blue) !important;
  background-image: linear-gradient(
    rgba(141, 135, 255, 0.12),
    rgba(141, 135, 255, 0.12)
  );
}

.modern-el-table :deep(.el-table__body .cell) {
  font-size: 13px;
}

/* A wide table is only obviously scrollable if its bar can be seen and grabbed.
   Element Plus hides it until hover and draws it 6px tall, which on the report
   tables reads as no bar at all. */
.modern-el-table :deep(.el-scrollbar__bar.is-horizontal) {
  height: 10px;
  opacity: 1 !important;
}
</style>
